import { FormEvent, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import {
  listInventoryItems,
  scanBarcode,
  type InventoryItemListRow,
} from '@/lib/api/inventory.js';
import { listRepairServices, type RepairService } from '@/lib/api/repair-services.js';
import { addLineItem, addRepair } from '@/lib/api/tickets.js';
import { getCurrentStore } from '@/lib/api/stores.js';
import { parseApiError } from '@/lib/api/errors.js';
import { formatCurrency } from '@/lib/format.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

type Mode = 'inventory' | 'service' | 'custom';

type Props = {
  ticketId: string;
  deviceId?: string | null;
  onAdded: () => void;
  onClose: () => void;
  /** Pre-fill inventory search (e.g. upsell product name) */
  initialSearch?: string;
  initialMode?: Mode;
};

export function CheckoutAddItemPanel({
  ticketId,
  deviceId,
  onAdded,
  onClose,
  initialSearch = '',
  initialMode = 'inventory',
}: Props): ReactElement {
  const queryClient = useQueryClient();
  const searchRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<Mode>(initialMode);
  const [search, setSearch] = useState(initialSearch);
  const debouncedSearch = useDebouncedValue(search, 250);
  const [qty, setQty] = useState('1');
  const [customDesc, setCustomDesc] = useState('');
  const [customPrice, setCustomPrice] = useState('0');

  useEffect(() => {
    searchRef.current?.focus();
  }, [mode]);

  const storeQuery = useQuery({ queryKey: ['store-settings'], queryFn: getCurrentStore });
  const negativeMode = storeQuery.data?.allowNegativeStock ?? 'warn';

  const itemsQuery = useQuery({
    queryKey: ['checkout-add-items', debouncedSearch],
    queryFn: () =>
      listInventoryItems({
        search: debouncedSearch,
        page: 1,
        pageSize: 12,
        sellableOnly: true,
      }),
    enabled: mode === 'inventory' && debouncedSearch.trim().length >= 2,
  });

  const servicesQuery = useQuery({
    queryKey: ['checkout-add-services', debouncedSearch],
    queryFn: () => listRepairServices({ search: debouncedSearch }),
    enabled: mode === 'service' && debouncedSearch.trim().length >= 2,
  });

  const addInventoryMut = useMutation({
    mutationFn: (item: InventoryItemListRow) =>
      addLineItem(ticketId, {
        description: item.name,
        type: item.category === 'repair_part' ? 'part' : 'retail',
        inventoryItemId: item.id,
        quantity: Math.max(0.01, Number(qty) || 1),
        unitPrice: Number(item.retailPrice ?? 0),
        discountAmount: 0,
        discountPct: 0,
        taxExempt: false,
      }),
    onSuccess: () => {
      toastSuccess('Item added to ticket');
      void queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      onAdded();
      setSearch('');
      setQty('1');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const addServiceMut = useMutation({
    mutationFn: (service: RepairService) =>
      addRepair(ticketId, {
        ...(deviceId ? { ticketDeviceId: deviceId } : {}),
        serviceId: service.id,
        serviceName: service.name,
        quotedPrice: Number(service.resolvedPrice ?? service.defaultPrice ?? 0),
      }),
    onSuccess: () => {
      toastSuccess('Service added');
      void queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      onAdded();
      setSearch('');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const addCustomMut = useMutation({
    mutationFn: () =>
      addLineItem(ticketId, {
        description: customDesc.trim(),
        type: 'custom',
        quantity: Math.max(0.01, Number(qty) || 1),
        unitPrice: Number(customPrice) || 0,
        discountAmount: 0,
        discountPct: 0,
        taxExempt: false,
      }),
    onSuccess: () => {
      toastSuccess('Custom line added');
      void queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      onAdded();
      setCustomDesc('');
      setCustomPrice('0');
      setQty('1');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const items = useMemo(() => itemsQuery.data?.data ?? [], [itemsQuery.data]);
  const services = useMemo(() => servicesQuery.data ?? [], [servicesQuery.data]);

  async function handleInventoryKey(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter' || mode !== 'inventory') return;
    event.preventDefault();
    const code = search.trim();
    if (!code) return;
    try {
      const result = await scanBarcode(code);
      if (result.type === 'item' && result.data?.id) {
        const raw = result.data;
        const item: InventoryItemListRow = {
          id: String(raw.id),
          name: String(raw.name ?? 'Item'),
          category: String(raw.category ?? 'retail_accessory'),
          qtyOnHand: String(raw.qtyOnHand ?? '0'),
          retailPrice: String(raw.retailPrice ?? '0'),
          lowStock: Boolean(raw.lowStock),
          barcodeInternal: (raw.barcodeInternal as string | null) ?? null,
        };
        addInventoryMut.mutate(item);
        return;
      }
    } catch {
      // fall through
    }
    const first = items[0];
    if (first) addInventoryMut.mutate(first);
    else toastError('No matching inventory item');
  }

  function stockWarning(item: InventoryItemListRow): string | null {
    const stock = Number(item.qtyAvailable ?? item.qtyOnHand ?? 0);
    if (stock > 0) return null;
    if (negativeMode === 'block') return 'Out of stock — cannot add';
    if (negativeMode === 'warn') return 'Out of stock — will allow with warning';
    return 'Out of stock (allowed)';
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-bold uppercase tracking-wide text-slate-600">Add to order</h3>
        <button type="button" className="rounded-lg p-1 text-slate-500 hover:bg-slate-200" onClick={onClose}>
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        {(
          [
            ['inventory', 'Inventory'],
            ['service', 'Service'],
            ['custom', 'Custom'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setMode(id)}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
              mode === id ? 'bg-primary text-white' : 'bg-white text-slate-700 border border-slate-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {mode !== 'custom' ? (
        <div className="relative mb-3">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input
            ref={searchRef}
            className="w-full rounded-xl border border-slate-300 py-2 pl-9 pr-3 text-sm"
            placeholder={
              mode === 'inventory'
                ? 'Scan barcode or search name / SKU…'
                : 'Search repair services…'
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleInventoryKey}
            autoComplete="off"
          />
        </div>
      ) : null}

      {mode === 'inventory' ? (
        <>
          <label className="mb-2 flex items-center gap-2 text-sm">
            <span className="font-medium text-slate-700">Qty</span>
            <input
              type="number"
              min="0.01"
              step="0.01"
              className="w-20 rounded-lg border border-slate-300 px-2 py-1 text-sm"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
          </label>
          <div className="max-h-48 space-y-1 overflow-auto">
            {itemsQuery.isFetching ? (
              <p className="text-sm text-slate-500">Searching…</p>
            ) : null}
            {debouncedSearch.trim().length >= 2 && !itemsQuery.isFetching && items.length === 0 ? (
              <p className="text-sm text-slate-500">No items found</p>
            ) : null}
            {items.map((item) => {
              const warn = stockWarning(item);
              const blocked = Number(item.qtyAvailable ?? item.qtyOnHand ?? 0) <= 0 && negativeMode === 'block';
              return (
                <button
                  key={item.id}
                  type="button"
                  disabled={blocked || addInventoryMut.isPending}
                  onClick={() => addInventoryMut.mutate(item)}
                  className="flex w-full items-start justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:opacity-50"
                >
                  <span className="min-w-0">
                    <span className="block font-medium text-slate-900">{item.name}</span>
                    <span className="block text-xs text-slate-500">
                      Stock {item.qtyAvailable ?? item.qtyOnHand}
                      {item.barcodeInternal ? ` · ${item.barcodeInternal}` : ''}
                    </span>
                    {warn ? <span className="block text-xs text-amber-700">{warn}</span> : null}
                  </span>
                  <span className="shrink-0 font-semibold">{formatCurrency(item.retailPrice)}</span>
                </button>
              );
            })}
          </div>
        </>
      ) : null}

      {mode === 'service' ? (
        <div className="max-h-48 space-y-1 overflow-auto">
          {servicesQuery.isFetching ? <p className="text-sm text-slate-500">Searching…</p> : null}
          {services.map((svc) => (
            <button
              key={svc.id}
              type="button"
              disabled={addServiceMut.isPending}
              onClick={() => addServiceMut.mutate(svc)}
              className="flex w-full items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm hover:bg-slate-50"
            >
              <span className="font-medium text-slate-900">{svc.name}</span>
              <span className="font-semibold">
                {formatCurrency(svc.resolvedPrice ?? svc.defaultPrice ?? 0)}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {mode === 'custom' ? (
        <div className="space-y-2">
          <input
            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            placeholder="Description"
            value={customDesc}
            onChange={(e) => setCustomDesc(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <input
              type="number"
              min="0"
              step="0.01"
              className="w-28 rounded-xl border border-slate-300 px-3 py-2 text-sm"
              placeholder="Price"
              value={customPrice}
              onChange={(e) => setCustomPrice(e.target.value)}
            />
            <input
              type="number"
              min="0.01"
              step="0.01"
              className="w-20 rounded-xl border border-slate-300 px-3 py-2 text-sm"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
            <button
              type="button"
              disabled={!customDesc.trim() || addCustomMut.isPending}
              onClick={() => addCustomMut.mutate()}
              className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              Add custom line
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
